import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { getTranslation } from '../../translations/translations';
import './ResetPassword.css';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const currentLanguage = localStorage.getItem('preferred-language') || 'en';
  const t = (key, fallback) => getTranslation(key, currentLanguage, fallback);

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await axios.get(`/api/auth/verify-reset-token/${token}`);
        setTokenValid(response.data.valid);
      } catch (err) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
      setTokenValid(false);
    }
  }, [token]);

  const validatePassword = (password) => {
    if (!password) {
      return t('passwordRequired', 'Password is required');
    }
    if (password.length < 6) {
      return t('passwordMinLength', 'Password must be at least 6 characters');
    }
    return '';
  };

  const validateConfirmPassword = (confirmPassword) => {
    if (!confirmPassword) {
      return t('confirmPasswordRequired', 'Please confirm your password');
    }
    if (confirmPassword !== formData.password) {
      return t('passwordsDoNotMatch', 'Passwords do not match');
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const passwordError = validatePassword(formData.password);
    const confirmError = validateConfirmPassword(formData.confirmPassword);

    if (passwordError || confirmError) {
      setErrors({
        password: passwordError,
        confirmPassword: confirmError
      });
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post('/api/auth/reset-password', {
        token,
        password: formData.password
      });
      setSuccess(true);

      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || t('genericError', 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-wrapper">
          <div className="verifying-state">
            <div className="loading-spinner large"></div>
            <p>{t('verifyingResetLink', 'Verifying reset link...')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-wrapper">
          <div className="reset-password-header">
            <div className="error-icon-large">&#10007;</div>
            <h2 className="reset-password-title">{t('invalidResetLink', 'Invalid Link')}</h2>
          </div>
          <div className="invalid-token-message">
            <p>{t('invalidResetLinkMessage', 'This password reset link is invalid or has expired.')}</p>
            <p className="note">{t('resetLinkExpiredNote', 'Reset links expire after 1 hour for security.')}</p>
          </div>
          <Link to="/forgot-password" className="request-new-link-button">
            {t('requestNewResetLink', 'Request New Reset Link')}
          </Link>
          <Link to="/" className="back-link">
            {t('backToLogin', 'Back to Login')}
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-wrapper">
          <div className="reset-password-header">
            <div className="success-icon">&#10003;</div>
            <h2 className="reset-password-title">{t('passwordResetSuccess', 'Password Reset!')}</h2>
          </div>
          <div className="success-message">
            <p>{t('passwordResetSuccessMessage', 'Your password has been successfully reset.')}</p>
            <p className="note">{t('redirectingToLogin', 'Redirecting to login page...')}</p>
          </div>
          <Link to="/" className="login-now-button">
            {t('loginNow', 'Login Now')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-wrapper">
        <div className="reset-password-header">
          <h2 className="reset-password-title">{t('resetPasswordTitle', 'Reset Password')}</h2>
          <p className="reset-password-subtitle">
            {t('resetPasswordSubtitle', 'Enter your new password below.')}
          </p>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">&#9888;</span>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="reset-password-form">
          <div className="input-group">
            <label className="input-label">{t('newPassword', 'New Password')}</label>
            <input
              type="password"
              name="password"
              placeholder={t('enterNewPassword', 'Enter new password')}
              value={formData.password}
              onChange={handleChange}
              className={`reset-password-input ${errors.password ? 'input-error' : ''}`}
              disabled={loading}
              autoComplete="new-password"
              autoFocus
            />
            {errors.password && (
              <span className="field-error">{errors.password}</span>
            )}
          </div>

          <div className="input-group">
            <label className="input-label">{t('confirmNewPassword', 'Confirm Password')}</label>
            <input
              type="password"
              name="confirmPassword"
              placeholder={t('confirmNewPasswordPlaceholder', 'Confirm new password')}
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`reset-password-input ${errors.confirmPassword ? 'input-error' : ''}`}
              disabled={loading}
              autoComplete="new-password"
            />
            {errors.confirmPassword && (
              <span className="field-error">{errors.confirmPassword}</span>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="submit-button"
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                {t('resetting', 'Resetting...')}
              </>
            ) : (
              t('resetPassword', 'Reset Password')
            )}
          </button>

          <Link to="/" className="back-link">
            {t('backToLogin', 'Back to Login')}
          </Link>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
